import { ArrowUpRight, Clapperboard, PenLine } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { scrollToId } from "@/lib/lenis";

const PRODUCTION_IMG =
  "https://images.unsplash.com/photo-1471341971476-ae15ff5dd4ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxmaWxtJTIwcHJvZHVjdGlvbiUyMGNhbWVyYSUyMHNldCUyMGxpZ2h0aW5nfGVufDB8fHx8MTc5MTg5MTA1NHww&ixlib=rb-4.1.0&q=85";

const PRODUCTION_TAGS = [
  "Complete Line Production",
  "Location Hunting & Recce",
  "Permissions & NOCs",
  "Vendor & Crew",
  "Equipment & Logistics",
  "Commercial Films",
  "Film & OTT",
];

const STORY_TAGS = [
  "Thought to Concept",
  "Concept to Story",
  "Screenplay & Dialogue",
  "SWA Writer Network",
  "Regional Languages",
  "Commercial Content",
];

function Tags({ items, prefix }) {
  return (
    <ul className="mt-8 flex flex-wrap gap-2.5">
      {items.map((t) => (
        <li
          key={t}
          data-testid={`${prefix}-tag-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className="border border-white/15 px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-300"
        >
          {t}
        </li>
      ))}
    </ul>
  );
}

export default function GoshtaCapabilities() {
  return (
    <section data-testid="goshta-capabilities" className="relative bg-[#050505] py-24 md:py-36">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]">
            Two Connected Capabilities
          </p>
          <h2 className="mt-5 max-w-3xl font-display text-3xl font-medium leading-tight text-neutral-100 sm:text-4xl lg:text-5xl">
            Built for the realities of production—and the power of a{" "}
            <em className="italic text-[#D4AF37]">good story.</em>
          </h2>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-neutral-400">
            Choose the service you need today. Bring both together when a project needs creative
            development and production execution under one roof.
          </p>
        </Reveal>

        <div id="production" className="mt-20 scroll-mt-24">
          <Reveal>
            <div className="group relative overflow-hidden border border-white/10 bg-[#0a0a0a]">
              <img
                src={PRODUCTION_IMG}
                alt="Studio camera and lighting setup"
                className="absolute inset-0 h-full w-full object-cover opacity-10 transition-opacity duration-700 group-hover:opacity-20"
              />
              <div className="relative grid grid-cols-1 gap-10 p-8 md:grid-cols-[auto_1fr] md:p-14">
                <div className="flex h-14 w-14 items-center justify-center border border-[#B79045]/50 text-[#D4AF37]">
                  <Clapperboard size={22} />
                </div>
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]">
                    01 / Video Production
                  </p>
                  <h3 className="mt-4 font-display text-2xl text-neutral-100 sm:text-3xl lg:text-4xl">
                    Production that <em className="italic">moves</em> with your project.
                  </h3>
                  <p className="mt-6 max-w-2xl text-base leading-relaxed text-neutral-400">
                    End-to-end on-ground production support across India—from location hunting and
                    recce to permissions, vendor management, crew coordination, equipment rentals,
                    logistics, budgeting, shoot management and wrap.
                  </p>
                  <Tags items={PRODUCTION_TAGS} prefix="production" />
                  <a
                    data-testid="production-brief-cta"
                    href="mailto:suyog@veritasadvisory.co.in?subject=Production%20Brief%20—%20Goshta"
                    className="mt-10 inline-flex items-center gap-2 border border-[#B79045]/60 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] transition-colors duration-300 hover:bg-[#B79045] hover:text-black"
                  >
                    Discuss a production brief <ArrowUpRight size={14} />
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        <div id="stories" className="mt-10 scroll-mt-24">
          <Reveal>
            <div className="relative border border-white/10 bg-[#0a0a0a]">
              <span
                aria-hidden="true"
                className="pointer-events-none absolute bottom-4 right-6 select-none font-display text-[10rem] italic leading-none text-white/[0.03] md:text-[16rem]"
              >
                Aa
              </span>
              <div className="relative grid grid-cols-1 gap-10 p-8 md:grid-cols-[auto_1fr] md:p-14">
                <div className="flex h-14 w-14 items-center justify-center border border-[#B79045]/50 text-[#D4AF37]">
                  <PenLine size={22} />
                </div>
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]">
                    02 / Story &amp; Creative Development
                  </p>
                  <h3 className="mt-4 font-display text-2xl text-neutral-100 sm:text-3xl lg:text-4xl">
                    Regional stories. <em className="italic">Wider possibilities.</em>
                  </h3>
                  <p className="mt-6 max-w-2xl text-base leading-relaxed text-neutral-400">
                    From a first thought to a complete concept, story, screenplay and dialogue, we
                    connect projects with suitable writers, including SWA writers, for film, series
                    and commercial content.
                  </p>
                  <Tags items={STORY_TAGS} prefix="story" />
                  <button
                    data-testid="story-develop-cta"
                    onClick={() => scrollToId("#writing-room")}
                    className="mt-10 inline-flex items-center gap-2 border border-[#B79045]/60 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] transition-colors duration-300 hover:bg-[#B79045] hover:text-black"
                  >
                    Develop a story with us <ArrowUpRight size={14} />
                  </button>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
